import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled, { keyframes } from 'styled-components'
import {
  CalendarDays,
  CalendarCheck,
  Clock,
  Phone,
  MoreVertical,
  ChevronRight,
} from 'lucide-react'
import { UserCreatedEvents } from '../../../../Data/event.dto'
import { useAppDispatch, useAppSelector } from '../../../../Redux/Hooks'
import { setUserCreatedEvent } from '../../../../Redux/Slice/Dashboard/EventSlice'
import { useNavigate } from 'react-router-dom'
import CalendarIconPNG from '../../../../assets/Icons/dateCalendar.svg'
import DeleteIconPNG from '../../../../assets/Icons/deleteEvent.svg'
import DeletePopup from '../Folder/DeletePopup'
import AddToCalendarModal from './AddToCalendarModal'
import {
  createEventAPI,
  deleteUserCreatedEventAPI,
} from '../../../../Redux/ApiCalls/Dashboard/EventAPI'
import {
  getAddedToCalendarIds,
  markEventAddedToCalendar,
  unmarkEventAddedToCalendar,
} from '../../../../Utils/inquiryCalendarStorage'
import { parse, format, addHours } from 'date-fns'
import { usePermissions } from '../../../../hooks/usePermissions'

interface UserCreatedEventCardProps {
  userEvent: UserCreatedEvents
  onCalendarStatusChange?: () => void
}

const gradient = 'linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)'

function getInitials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?'
}

const UserCreatedEventCard: React.FC<UserCreatedEventCardProps> = ({
  userEvent,
  onCalendarStatusChange,
}) => {
  const menuRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const userId = useAppSelector((state) => state.auth.user?.id)
  const [isDeletePopUp, setIsDeletePopup] = useState(false)
  const [isAddToCalendarPopUp, setIsAddToCalendarPopUp] = useState(false)
  const [alreadyInCalendar, setAlreadyInCalendar] = useState(false)
  const [dropDownMenu, setDropDownMenu] = useState(false)
  const { hasPermission } = usePermissions()
  const canCreate = hasPermission('events.create')
  const canDelete = hasPermission('events.delete')
  const hasAnyAction = canCreate || canDelete

  const [isAlreadyAdded, setIsAlreadyAdded] = useState(() =>
    getAddedToCalendarIds(userId).includes(userEvent.id)
  )

  useEffect(() => {
    setIsAlreadyAdded(getAddedToCalendarIds(userId).includes(userEvent.id))
  }, [userId, userEvent.id])

  const notifyCalendarStatusChange = () => {
    onCalendarStatusChange?.()
  }

  const openMenu = (e: React.MouseEvent) => {
    e.stopPropagation()
    setDropDownMenu((pre) => !pre)
  }

  function getEventDates(event: UserCreatedEvents) {
    if (!event.sub_events || event.sub_events.length === 0) {
      return { starting_date: null, ending_date: null }
    }

    let minStartingDate = event.sub_events[0].starting_date
    let maxEndingDate = event.sub_events[0].starting_date

    event.sub_events.forEach((subEvent) => {
      if (subEvent.starting_date < minStartingDate) {
        minStartingDate = subEvent.starting_date
      }
      if (subEvent.starting_date > maxEndingDate) {
        maxEndingDate = subEvent.starting_date
      }
    })

    return {
      starting_date: minStartingDate,
      ending_date: maxEndingDate,
    }
  }

  const handleViewFullEvent = () => {
    dispatch(setUserCreatedEvent(userEvent))
    navigate('/dashboard/events/userCreated/single')
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setDropDownMenu(false)
    }
  }

  useEffect(() => {
    if (dropDownMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [dropDownMenu])

  const handleDeleteEvent = () => {
    unmarkEventAddedToCalendar(userEvent.id, userId)
    notifyCalendarStatusChange()
    dispatch(deleteUserCreatedEventAPI({ event_id: userEvent.id }))
    setIsDeletePopup(false)
  }

  const openAddToCalendar = () => {
    setDropDownMenu(false)
    const alreadyAdded = getAddedToCalendarIds(userId).includes(userEvent.id)
    setIsAlreadyAdded(alreadyAdded)
    setAlreadyInCalendar(alreadyAdded)
    setIsAddToCalendarPopUp(true)
  }

  const convertHourToEndDate = (
    hour: number,
    start_date: string,
    start_time: string
  ) => {
    const startDateTime = `${start_date}T${start_time}`
    const referenceDate: Date = new Date() as Date
    const parsedStartDate = parse(
      startDateTime,
      "yyyy-MM-dd'T'HH:mm:ss",
      referenceDate
    ) as Date
    const end = addHours(parsedStartDate, hour)
    const end_date = format(end, 'yyyy-MM-dd')
    const end_time = format(end, 'HH:mm:ss')
    return { end_date, end_time }
  }

  const handleAddCalenderEvent = async () => {
    if (getAddedToCalendarIds(userId).includes(userEvent.id)) {
      setAlreadyInCalendar(true)
      return
    }

    const subEventsData = userEvent.sub_events.map((event) => {
      const { end_date, end_time } = convertHourToEndDate(
        event.duration,
        event.starting_date,
        event.starting_time
      )
      return {
        sub_event_name: event.sub_event_name,
        start_time: event.starting_time,
        end_date,
        end_time,
        start_date: event.starting_date,
        location: event.sub_event_location,
        sub_event_member_id: [],
      }
    })

    // Mark before API so the badge survives the list remount when createEventAPI toggles loading
    markEventAddedToCalendar(userEvent.id, userId)
    setIsAlreadyAdded(true)
    setIsAddToCalendarPopUp(false)
    setAlreadyInCalendar(false)
    setDropDownMenu(false)
    notifyCalendarStatusChange()

    try {
      const payload: any = {
        title: userEvent.event_name,
        customer_name: userEvent.customer_name,
        customer_phone: userEvent.phone_number,
        customer_country_code: '+91',
        sub_events: subEventsData,
      }
      if (userEvent.branch_id) {
        payload.branch_id = userEvent.branch_id
      }
      await dispatch(createEventAPI(payload)).unwrap()
    } catch {
      unmarkEventAddedToCalendar(userEvent.id, userId)
      setIsAlreadyAdded(false)
      notifyCalendarStatusChange()
    }
  }

  const dates = getEventDates(userEvent)
  const subEventCount = userEvent.sub_events?.length ?? 0
  const hasMultipleSubEvents = subEventCount > 1

  return (
    <>
      {createPortal(
        <>
          {isDeletePopUp && (
            <DeletePopup
              text="Are you sure you want to delete this event?"
              cancel={() => setIsDeletePopup(false)}
              Delete={() => handleDeleteEvent()}
              buttonText="Delete"
            />
          )}
          <AddToCalendarModal
            isOpen={isAddToCalendarPopUp}
            alreadyAdded={alreadyInCalendar}
            onCancel={() => {
              setIsAddToCalendarPopUp(false)
              setAlreadyInCalendar(false)
              setDropDownMenu(false)
            }}
            onConfirm={() => handleAddCalenderEvent()}
          />
        </>,
        document.body
      )}

      <Card>
        <CardTop>
          <CustomerRow>
            <Avatar>{getInitials(userEvent.customer_name)}</Avatar>
            <CustomerInfo>
              <CustomerName>{userEvent.customer_name}</CustomerName>
              <PhoneRow>
                <Phone size={11} strokeWidth={2} />
                {userEvent.phone_number}
              </PhoneRow>
            </CustomerInfo>
          </CustomerRow>

          <CardActions>
            {isAlreadyAdded && (
              <InCalendarBadge
                type="button"
                title="This event is already in your calendar"
                aria-label="Already in calendar"
                onClick={(e) => {
                  e.stopPropagation()
                  setAlreadyInCalendar(true)
                  setIsAddToCalendarPopUp(true)
                }}
              >
                <CalendarCheck size={14} strokeWidth={2.4} />
                <span>In calendar</span>
              </InCalendarBadge>
            )}
            {hasAnyAction && (
              <MenuWrap>
                <MenuButton ref={buttonRef} type="button" onClick={openMenu} aria-label="Event options">
                  <MoreVertical size={16} />
                </MenuButton>
                <DropdownMenu $open={dropDownMenu} ref={menuRef}>
                  {canCreate && (
                    <MenuItem onClick={openAddToCalendar}>
                      <ItemIcon src={CalendarIconPNG} alt="" />
                      <ItemName>
                        {isAlreadyAdded ? 'Already In Calendar' : 'Add To Calendar'}
                      </ItemName>
                    </MenuItem>
                  )}
                  {canCreate && canDelete && <HrMenu />}
                  {canDelete && (
                    <MenuItem
                      onClick={() => {
                        setDropDownMenu(false)
                        setIsDeletePopup(true)
                      }}
                    >
                      <ItemIcon src={DeleteIconPNG} alt="" />
                      <ItemName>Delete Event</ItemName>
                    </MenuItem>
                  )}
                </DropdownMenu>
              </MenuWrap>
            )}
          </CardActions>
        </CardTop>

        <EventBlock>
          <EventTitleRow>
            <EventTitle>{userEvent.event_name}</EventTitle>
            {userEvent.branch_name && (
              <span style={{ fontSize: 11, background: '#e0e7ff', color: '#4338ca', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>
                {userEvent.branch_name}
              </span>
            )}
            {subEventCount > 0 && (
              <SubCount>
                {subEventCount} sub-event{subEventCount !== 1 ? 's' : ''}
              </SubCount>
            )}
          </EventTitleRow>

          <InfoRow>
            <InfoCell>
              <InfoIcon>
                <CalendarDays size={14} strokeWidth={2} />
              </InfoIcon>
              <InfoText>
                <InfoLabel>{hasMultipleSubEvents ? 'From' : 'Date'}</InfoLabel>
                <InfoValue>{dates.starting_date ?? '—'}</InfoValue>
              </InfoText>
            </InfoCell>

            <InfoCell>
              <InfoIcon>
                <Clock size={14} strokeWidth={2} />
              </InfoIcon>
              <InfoText>
                <InfoLabel>{hasMultipleSubEvents ? 'To' : 'Duration'}</InfoLabel>
                <InfoValue>
                  {hasMultipleSubEvents
                    ? dates.ending_date ?? '—'
                    : subEventCount > 0
                      ? `${userEvent.sub_events[0].duration} hours`
                      : '—'}
                </InfoValue>
              </InfoText>
            </InfoCell>
          </InfoRow>
        </EventBlock>

        <ViewBtn type="button" onClick={handleViewFullEvent}>
          <ViewBtnText>See Full Event</ViewBtnText>
          <ChevronRight size={15} />
        </ViewBtn>
      </Card>
    </>
  )
}

export default UserCreatedEventCard

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`

const Card = styled.article`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 20px;
  background: #fff;
  border-radius: 18px;
  border: 1px solid rgba(23, 23, 23, 0.07);
  box-shadow: 0 2px 12px rgba(23, 23, 23, 0.04);
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${gradient};
    opacity: 0.65;
  }

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(201, 77, 170, 0.14);
    box-shadow: 0 8px 24px rgba(23, 23, 23, 0.07);
  }
`

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`

const CustomerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
`

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #9a4d87;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.22) 0%,
    rgba(201, 77, 170, 0.14) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
`

const CustomerInfo = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`

const CustomerName = styled.h3`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #171717;
  line-height: 1.25;
  text-transform: capitalize;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const PhoneRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.5);
`

const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`

const InCalendarBadge = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid rgba(53, 183, 200, 0.28);
  background: rgba(53, 183, 200, 0.1);
  color: #1f8f9c;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(53, 183, 200, 0.16);
    border-color: rgba(53, 183, 200, 0.4);
  }
`

const MenuWrap = styled.div`
  position: relative;
  flex-shrink: 0;
`

const MenuButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 9px;
  border: 1px solid rgba(23, 23, 23, 0.07);
  background: #fafafa;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(23, 23, 23, 0.45);
  transition: all 0.15s ease;
  &:hover {
    color: #c94daa;
    border-color: rgba(201, 77, 170, 0.2);
    background: rgba(201, 77, 170, 0.05);
  }
`

const DropdownMenu = styled.div<{ $open: boolean }>`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 190px;
  background: #fff;
  border-radius: 12px;
  padding: 5px;
  z-index: 10;
  display: ${({ $open }) => ($open ? 'block' : 'none')};
  animation: ${({ $open }) => ($open ? fadeIn : 'none')} 0.15s ease-out;
  box-shadow: 0 10px 32px rgba(23, 23, 23, 0.1);
  border: 1px solid rgba(23, 23, 23, 0.08);
`

const MenuItem = styled.div`
  padding: 10px 16px;
  cursor: pointer;
  color: #111827;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 14px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f3f4f6;
  }
`

const ItemIcon = styled.img`
  width: 20px;
  height: 20px;
`

const ItemName = styled.p`
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  margin: 0;
  color: inherit;
`

const HrMenu = styled.hr`
  margin: 4px 0;
  border: none;
  height: 1px;
  background-color: rgba(17, 24, 39, 0.08);
`

const EventBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const EventTitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`

const EventTitle = styled.h4`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #171717;
  line-height: 1.35;
  flex: 1;
  min-width: 0;
`

const SubCount = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.4);
  white-space: nowrap;
`

const InfoRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`

const InfoCell = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 11px 12px;
  border-radius: 12px;
  background: rgba(23, 23, 23, 0.025);
  border: 1px solid rgba(23, 23, 23, 0.05);
`

const InfoIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  flex-shrink: 0;
  color: rgba(201, 77, 170, 0.75);
  background: rgba(201, 77, 170, 0.08);
`

const InfoText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const InfoLabel = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.42);
  text-transform: uppercase;
  letter-spacing: 0.03em;
`

const InfoValue = styled.span`
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #171717;
  line-height: 1.3;
  word-break: break-word;
`

const ViewBtn = styled.button`
  height: 42px;
  width: 100%;
  border-radius: 11px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid transparent;
  background:
    linear-gradient(#fff, #fff) padding-box,
    ${gradient} border-box;
  transition: all 0.2s ease;

  svg {
    color: #c94daa;
    transition: color 0.2s ease;
  }

  &:hover {
    background: ${gradient};
    svg { color: #fff; }
    span {
      color: #fff;
      -webkit-text-fill-color: #fff;
      background: none;
    }
  }
`

const ViewBtnText = styled.span`
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 600;
  background: ${gradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`
